import { useEffect, useContext, useRef } from "react";
import styles from "./HeroSectionStyles.module.css";
import { GlobalStateContext } from "../../context/GlobalStateContext";

function HeroSection() {
  const { titleData } = useContext(GlobalStateContext);
  const svgRef = useRef(null);

  // unique suffix per component instance (stable for lifetime)
  const uidRef = useRef(
    (`uid-${Math.random().toString(16).slice(2)}`)
      .replaceAll(":", "-")
  );

  useEffect(() => {
    const svg = svgRef.current;
    if (!svg) return;

    // If StrictMode mounts/unmounts twice, we still want clean init
    let raf = 0;
    let destroyed = false;

    const uid = uidRef.current;
    const maskId = `netFadeMask-${uid}`;
    const gradId = `netFadeGrad-${uid}`;

    // ---- Tunables ----
    const W = 1000, H = 350;
    const NODE_COUNT = 100;
    const MAX_LINK_DIST = 200;
    const MAX_LINKS_PER_NODE = 6;

    const SPEED = 1;
    const DRIFT = 20;

    const BASE_R = 0.5;
    const DEPTH_R = 2;

    const LINE_ALPHA = 0.22;
    const NODE_ALPHA = 0.65;

    const LINE_REL = 0.28;
    const MIN_LINE = 0.25;
    const MAX_LINE = 1.2;

    const FG = "#3aa26a";

    // helpers
    const ns = "http://www.w3.org/2000/svg";
    const el = (name, attrs = {}) => {
      const e = document.createElementNS(ns, name);
      for (const k in attrs) e.setAttribute(k, attrs[k]);
      return e;
    };
    const clamp = (v, a, b) => Math.max(a, Math.min(b, v));
    const dist2 = (a, b) => {
      const dx = a.x - b.x, dy = a.y - b.y;
      return dx * dx + dy * dy;
    };

    // reset svg safely
    svg.setAttribute("viewBox", `0 0 ${W} ${H}`);
    while (svg.firstChild) svg.removeChild(svg.firstChild);

    // defs + mask (white = visible, transparent = hidden)
    const defs = el("defs");

    const fadeGrad = el("linearGradient", {
      id: gradId,
      x1: "0", y1: "0",
      x2: "0", y2: "1",
      gradientUnits: "objectBoundingBox"
    });

    // Fade zones: adjust 12% / 88% to change fade height
    fadeGrad.appendChild(el("stop", { offset: "0%", "stop-color": "white", "stop-opacity": "0" }));
    fadeGrad.appendChild(el("stop", { offset: "12%", "stop-color": "white", "stop-opacity": "1" }));
    fadeGrad.appendChild(el("stop", { offset: "88%", "stop-color": "white", "stop-opacity": "1" }));
    fadeGrad.appendChild(el("stop", { offset: "100%", "stop-color": "white", "stop-opacity": "0" }));

    const mask = el("mask", {
      id: maskId,
      maskUnits: "userSpaceOnUse",
      x: "0",
      y: "0",
      width: String(W),
      height: String(H)
    });

    mask.appendChild(
      el("rect", {
        x: 0, y: 0, width: W, height: H,
        fill: `url(#${gradId})`
      })
    );

    defs.appendChild(fadeGrad);
    defs.appendChild(mask);
    svg.appendChild(defs);

    // Container group for the network (mask is applied here)
    const gNet = el("g", { mask: `url(#${maskId})` });
    svg.appendChild(gNet);

    const gLines = el("g", { opacity: String(LINE_ALPHA) });
    const gNodes = el("g", { opacity: String(NODE_ALPHA) });
    gNet.appendChild(gLines);
    gNet.appendChild(gNodes);

    // Nodes (biased distribution like your reference)
    const nodes = Array.from({ length: NODE_COUNT }, () => {
      const x = Math.pow(Math.random(), 0.65) * W;
      const y = clamp(Math.pow(Math.random(), 1.15) * H, 0, H);
      return {
        x, y, ox: x, oy: y,
        ax: Math.random() * Math.PI * 2,
        ay: Math.random() * Math.PI * 2,
        az: Math.random() * Math.PI * 2,
        s: 0.6 + Math.random() * 0.9,
      };
    });

    // Build edges by proximity
    const links = [];
    const neighborCounts = new Array(NODE_COUNT).fill(0);
    const maxD2 = MAX_LINK_DIST * MAX_LINK_DIST;

    for (let i = 0; i < NODE_COUNT; i++) {
      const candidates = [];
      for (let j = i + 1; j < NODE_COUNT; j++) {
        const d2 = dist2(nodes[i], nodes[j]);
        if (d2 <= maxD2) candidates.push([d2, j]);
      }
      candidates.sort((a, b) => a[0] - b[0]);
      for (const [, j] of candidates) {
        if (neighborCounts[i] >= MAX_LINKS_PER_NODE) break;
        if (neighborCounts[j] >= MAX_LINKS_PER_NODE) continue;
        neighborCounts[i]++; neighborCounts[j]++;
        links.push([i, j]);
      }
    }

    // Two line segments per link so ends can vary thickness
    const lineSegs = links.map(([i, j]) => {
      const a = el("line", { stroke: FG, "stroke-linecap": "round" });
      const b = el("line", { stroke: FG, "stroke-linecap": "round" });
      gLines.appendChild(a);
      gLines.appendChild(b);
      return { i, j, a, b };
    });

    const circles = nodes.map(() => {
      const c = el("circle", { fill: FG });
      gNodes.appendChild(c);
      return c;
    });

    const t0 = performance.now();

    function frame(now) {
      if (destroyed) return;

      const t = ((now - t0) * 0.001) * SPEED;
      const radii = new Array(NODE_COUNT);

      for (let i = 0; i < NODE_COUNT; i++) {
        const n = nodes[i];

        const dx =
          (Math.sin(t + n.ax) + 0.55 * Math.sin(1.7 * t + 1.3 * n.ax)) *
          DRIFT * n.s;
        const dy =
          (Math.cos(t + n.ay) + 0.55 * Math.cos(1.6 * t + 1.1 * n.ay)) *
          DRIFT * n.s;

        const z = 0.5 + 0.5 * Math.sin(1.2 * t + n.az);
        const r = BASE_R + z * DEPTH_R;

        n.x = clamp(n.ox + dx, -40, W + 40);
        n.y = clamp(n.oy + dy, -40, H + 40);
        radii[i] = r;

        const c = circles[i];
        c.setAttribute("cx", n.x);
        c.setAttribute("cy", n.y);
        c.setAttribute("r", r);
      }

      for (const seg of lineSegs) {
        const n1 = nodes[seg.i], n2 = nodes[seg.j];
        const mx = (n1.x + n2.x) * 0.5;
        const my = (n1.y + n2.y) * 0.5;

        const r1 = radii[seg.i], r2 = radii[seg.j];

        // Always keep lines thinner than nodes
        const w1 = clamp(r1 * LINE_REL, MIN_LINE, Math.min(MAX_LINE, r1 - 0.15));
        const w2 = clamp(r2 * LINE_REL, MIN_LINE, Math.min(MAX_LINE, r2 - 0.15));

        seg.a.setAttribute("x1", n1.x);
        seg.a.setAttribute("y1", n1.y);
        seg.a.setAttribute("x2", mx);
        seg.a.setAttribute("y2", my);
        seg.a.setAttribute("stroke-width", w1);

        seg.b.setAttribute("x1", mx);
        seg.b.setAttribute("y1", my);
        seg.b.setAttribute("x2", n2.x);
        seg.b.setAttribute("y2", n2.y);
        seg.b.setAttribute("stroke-width", w2);
      }

      raf = requestAnimationFrame(frame);
    }

    raf = requestAnimationFrame(frame);

    return () => {
      destroyed = true;
      if (raf) cancelAnimationFrame(raf);
      // optional: clear SVG to avoid stray defs in dev hot reload
      while (svg.firstChild) svg.removeChild(svg.firstChild);
    };
  }, []);

  return (
    <section className={styles.heroSection}>
      <svg
        ref={svgRef}
        className={styles.net}
        viewBox="0 0 1000 350"
        preserveAspectRatio="xMidYMid slice"
        aria-hidden="true"
      />
      <div className={styles.content}>
        <h1>Your AI gateway to investor insights on the {titleData}.</h1>
        <p>
          Discover and engage with the latest reports and updates from the
          world’s leading companies.
        </p>
      </div>
    </section>
  );
}

export default HeroSection;